/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import { Helmet } from "react-helmet-async";
import DivorcePlannerNavigation from "@/src/components/DivorcePlanner/DivorcePlannerNavigation";
import DivorcePlannerProcess from "@/src/components/DivorcePlanner/DivorcePlannerProcess";
import { STATUS_DATA_COMPLETED, STATUS_POWER_OF_ATTORNEY_GRANTED } from "@/src/services/divorce/leadStatus";
import DivorcePlannerChildren from "@/src/components/DivorcePlanner/DivorcePlannerChildren";
import { DivorcePlannerChecklists } from "@/src/components/DivorcePlanner/DivorcePlannerChecklists";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import _get from "lodash/get";
import { getCaseLink, getProductData } from "@/src/services/productService";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import useProductParams from "@/src/hooks/useProductParams";
import { Stack } from "@mui/material";
import { gaSet } from "@/src/services/googleAnalyticsService";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import LinkLegalbird from "@/src/components/legalbird/LinkLegalbird/LinkLegalbird";

const DivorcePlannerPage = () => {
  const { productId } = useProductParams();
  const productApiUri = getProductData("divorce", "apiUri");
  const { data: divorce, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data),
  });

  const isAccessible = () => {
    return (
      divorce.leadStatus >= STATUS_POWER_OF_ATTORNEY_GRANTED || (_get(divorce, "caseInsurance.insurer.id") > 0 && divorce.leadStatus >= STATUS_DATA_COMPLETED)
    );
  };

  const saveChecklist = (checklist: { divorce: { [key: string]: boolean } }) => {
    updateMutation.mutate({
      uri: "/divorces",
      id: divorce.id,
      data: {
        checklist: checklist,
      },
    });
  };

  const title = "Mein Scheidungsplaner | Legalbird";
  gaSet({ title: title });
  if (isLoading) {
    return (
      <LayoutProtectedRoutes containerVariant={"sm"}>
        <DivorcePlannerHeader />
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  const accessible = isAccessible();
  return (
    <LayoutProtectedRoutes containerVariant={"sm"}>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Stack spacing={2}>
        <DivorcePlannerHeader />
        <DivorcePlannerNavigation divorce={divorce} accessible={accessible} />
        {accessible && (
          <>
            <DivorcePlannerProcess divorce={divorce} id={"ablauf"} />
            <DivorcePlannerChecklists
              divorce={divorce}
              saveChecklist={saveChecklist}
              id={"checkliste"}
              hasInsurance={_get(divorce, "caseInsurance.insurer.id") > 0}
            />
            {divorce.numberChildren > 0 && <DivorcePlannerChildren id={"kinder"} />}
            <LinkLegalbird to={getCaseLink(divorce)} standalone sx={{ alignSelf: "flex-start" }}>
              Zurück zum Fall
            </LinkLegalbird>
          </>
        )}
      </Stack>
    </LayoutProtectedRoutes>
  );
};

const DivorcePlannerHeader = () => (
  <Typography component={"h1"} variant={"h5"} sx={{ paddingTop: "2.5rem", marginBottom: "1rem" }}>
    Mein Scheidungsplaner
  </Typography>
);

export default DivorcePlannerPage;
